import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import httpService from "../../../services/http/stores/outlet.service";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import UnlimitToggle from "../../../components/ui-kit/unlimit-toggle";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import { all_routes } from "../../../Router/all_routes";
import UnlimitInput from "../../../components/ui-kit/unlimit-input";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import ConfirmationAlert from "../../../components/confirm-alert";
import notificationService from "../../../services/toaster.service";
import { hydrateErrors } from "../../../utils/error.utils";

const CCTVForm = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State management
  const [cctvList, setCCTVList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState("");
  const [nextTempId, setNextTempId] = useState(1); // Track next temp ID

  const {
    register,
    setValue,
    watch,
    formState,
    handleSubmit,
    setError,
    unregister,
  } = useForm();
  const { errors } = formState;

  // Load CCTV data on component mount
  useEffect(() => {
    if (id) {
      getCCTV();
    }
  }, [id]);

  // Fetch CCTV data
  const getCCTV = useCallback(async () => {
    setLoading(true);
    try {
      const response = await httpService.shopCameraList(id);
      if (response.status === 200) {
        const data = response.data.data.data;
        setCCTVList(data);

        // Set form values for NVR settings
        const outletData = data[0]?.outlet;
        if (outletData) {
          setValue("nvr_url", outletData.nvr_url);
          setValue("nvr_username", outletData.nvr_username);
          setValue("nvr_password", outletData.nvr_password);
        }

        // Set form values for cameras
        data.forEach((item) => {
          setValue(`ip_address_${item.id}`, item.ip_address);
          setValue(`note_${item.id}`, item.note);
          setValue(`is_active_${item.id}`, item.is_active);
        });

        // Set next temp ID based on existing data
        const maxTempId = data
          .filter((item) => String(item.id).startsWith("temp-"))
          .reduce((max, item) => {
            const tempNum = parseInt(String(item.id).replace("temp-", ""), 10);
            return Math.max(max, tempNum);
          }, 0);
        setNextTempId(maxTempId + 1);
      }
    } catch (error) {
      console.error("Error fetching CCTV data:", error);
      notificationService.errorMessage(t("general.error.fetchFailed"));
    } finally {
      setLoading(false);
    }
  }, [id, setValue, t]);

  // Generate unique camera title based on existing cameras
  const generateCameraTitle = useCallback((existingCameras) => {
    const existingNumbers = existingCameras
      .map((cam) => {
        const match = cam.title.match(/Cam (\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => num > 0);

    let nextNumber = 1;
    while (existingNumbers.includes(nextNumber)) {
      nextNumber++;
    }
    return `Cam ${nextNumber}`;
  }, []);

  // Add new camera rows
  const handleAddRows = useCallback(() => {
    const count = parseInt(rowCount, 10);

    if (isNaN(count) || count <= 0) {
      notificationService.errorMessage(t("general.error.invalidNumber"));
      return;
    }

    if (count > 50) {
      // Reasonable limit
      notificationService.errorMessage(t("general.error.tooManyCameras"));
      return;
    }

    const newRows = [];
    let currentTempId = nextTempId;

    for (let i = 0; i < count; i++) {
      const newId = `temp-${currentTempId}`;
      const title = generateCameraTitle([...cctvList, ...newRows]);

      newRows.push({
        id: newId,
        title,
        ip_address: "",
        note: "",
        is_active: true, // Default to active
      });

      // Set default form values for new cameras
      setValue(`ip_address_${newId}`, "");
      setValue(`note_${newId}`, "");
      setValue(`is_active_${newId}`, true);

      currentTempId++;
    }

    setCCTVList((prevList) => [...prevList, ...newRows]);
    setNextTempId(currentTempId);
    setRowCount(""); // Clear input after adding
  }, [rowCount, cctvList, nextTempId, generateCameraTitle, setValue, t]);

  // Delete camera row
  const handleDeleteRow = useCallback(
    (rowId) => {
      setCCTVList((prevList) => prevList.filter((row) => row.id !== rowId));

      // Unregister form fields for deleted row
      unregister(`ip_address_${rowId}`);
      unregister(`note_${rowId}`);
      unregister(`is_active_${rowId}`);
    },
    [unregister]
  );

  // Form validation rules
  const validationRules = useMemo(
    () => ({
      nvr_url: {
        pattern: {
          value:
            /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:\d{1,3}(?:\.\d{1,3}){3}|(?:[a-z0-9\u00a1-\uffff-]+\.)+[a-z\u00a1-\uffff]{2,})(?::\d{2,5})?(?:[/?#]\S*)?$/i,
          message: t("inputField.validation.invalidURL"),
        },
      },
    }),
    [t]
  );

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: t("tableColumnName.srNo"),
        dataIndex: "index",
        align: "center",
        render: (value, item, index) => index + 1,
      },
      {
        title: t("cctv.cameraTitle"),
        dataIndex: "title",
        render: (text, record) => <span className="fw-medium">{text}</span>,
      },
      {
        title: t("cctv.IPAddress"),
        dataIndex: "ip_address",
        render: (text, record, index) => (
          <UnlimitInput
            id={`ip_address_${record.id}`}
            type="text"
            placeholder={t("inputField.placeholder.enterIPAddress")}
            register={register}
            rules={{
              pattern: {
                value:
                  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                message: t("inputField.validation.invalidIPAddress"),
              },
            }}
            /*   error={errors[`ip_address_${record.id}`]}
            errorMessage={errors[`ip_address_${record.id}`]?.message} */
            error={errors[`cameras.${index}.ip_address`]}
            errorMessage={errors[`cameras.${index}.ip_address`]?.message}
          />
        ),
      },
      {
        title: t("cctv.note"),
        dataIndex: "note",
        render: (text, record) => (
          <UnlimitInput
            id={`note_${record.id}`}
            type="text"
            placeholder={t("inputField.placeholder.enterNote")}
            register={register}
            rules={{
              maxLength: {
                value: 255,
                message: t("inputField.validation.maxLength", { max: 255 }),
              },
            }}
            error={errors[`note_${record.id}`]}
            errorMessage={errors[`note_${record.id}`]?.message}
          />
        ),
      },
      {
        title: t("tableColumnName.status"),
        dataIndex: "is_active",
        render: (text, record) => (
          <UnlimitToggle
            label={t("tableColumnName.status")}
            id={`is_active_${record.id}`}
            register={register}
            checked={watch(`is_active_${record.id}`)}
            onChange={(e) =>
              setValue(`is_active_${record.id}`, e.target.checked)
            }
          />
        ),
      },
      {
        title: t("tableColumnName.actions"),
        dataIndex: "actions",
        render: (_, record) => (
          <ConfirmationAlert
            onConfirm={() => handleDeleteRow(record.id)}
            title={t("general.confirm.deleteTitle")}
            message={t("general.confirm.deleteMessage")}
          />
        ),
      },
    ],
    [t, register, errors, watch, setValue, handleDeleteRow]
  );

  // Breadcrumb configuration
  const breadcrumbItems = useMemo(
    () => [
      { label: "", link: "/", icon: "feather-home" },
      { label: t("cctv.cctv"), link: "/cctv-list" },
      { label: id ? t("cctv.editCCTV") : t("cctv.addCCTV"), active: true },
    ],
    [t, id]
  );

  // Form submission handler
  const onSubmitForm = useCallback(
    async (data) => {
      if (cctvList.length === 0) {
        notificationService.errorMessage(t("cctv.error.noCameras"));
        return;
      }

      const formattedData = {
        outlet_id: id,
        nvr_url: data.nvr_url,
        nvr_username: data.nvr_username,
        nvr_password: data.nvr_password,
        cameras: cctvList.map((cam) => {
          const cameraData = {
            title: cam.title,
            ip_address: data[`ip_address_${cam.id}`] || "",
            is_active: data[`is_active_${cam.id}`] || false,
            note: data[`note_${cam.id}`] || "",
          };

          // Include ID only for existing cameras (not temp ones)
          if (!String(cam.id).startsWith("temp-")) {
            cameraData.id = cam.id;
          }

          return cameraData;
        }),
      };

      setIsLoading(true);

      try {
        const response = await httpService.shopCameraUpsert(formattedData);
        if (response.status === 200) {
          notificationService.successMessage(t("cctv.cctvUpdateSuccess"));
          setTimeout(() => {
            navigate(all_routes.cctvList);
          }, 1000);
        }
      } catch (error) {
        if (error.validation_error?.errors) {
          // hydrateErrors(error.validation_error.errors, setError);
        } else {
          notificationService.errorMessage(t("general.error.updateFailed"));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [cctvList, id, t, navigate, setError]
  );

  // Get outlet info for display
  const outletInfo = useMemo(() => {
    return cctvList[0]?.outlet || null;
  }, [cctvList]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex flex-column">
            <div className="page-title">
              <h3 className="text-black">
                {id ? t("cctv.editCCTV") : t("cctv.addCCTV")}
              </h3>
            </div>
            <UnlimitBreadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className="card">
          <div className="card-body pb-0">
            {/* Outlet Information */}
            <div className="mb-4">
              {outletInfo?.image_url && outletInfo?.name && (
                <div className="d-flex align-items-center">
                  <img
                    src={outletInfo.image_url}
                    alt={outletInfo.name}
                    className="me-3 rounded"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                    }}
                  />
                  <div>
                    <h5 className="mb-1">{outletInfo.name}</h5>
                    {outletInfo.address && (
                      <p className="text-muted mb-0">{outletInfo.address}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit(onSubmitForm)}>
              {/* NVR Configuration */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <UnlimitInput
                    label={t("inputField.label.NVRURL")}
                    id="nvr_url"
                    type="text"
                    placeholder={t("inputField.placeholder.enterNVRURL")}
                    register={register}
                    rules={validationRules.nvr_url}
                    error={errors.nvr_url}
                    errorMessage={errors.nvr_url?.message}
                  />
                </div>
                <div className="col-md-4">
                  <UnlimitInput
                    label={t("inputField.label.userName")}
                    id="nvr_username"
                    type="text"
                    placeholder={t("inputField.placeholder.enterNVRUsername")}
                    register={register}
                    error={errors.nvr_username}
                    errorMessage={errors.nvr_username?.message}
                  />
                </div>
                <div className="col-md-4">
                  <UnlimitInput
                    label={t("inputField.label.password")}
                    id="nvr_password"
                    type="password"
                    placeholder={t("inputField.placeholder.enterNVRPassword")}
                    register={register}
                    error={errors.nvr_password}
                    errorMessage={errors.nvr_password?.message}
                  />
                </div>
              </div>

              {/* Add Cameras Section */}
              <div className="d-flex align-items-center justify-content-end">
                <input
                  type="number"
                  className="form-control me-2"
                  style={{ width: "200px" }}
                  value={rowCount}
                  onChange={(e) => setRowCount(e.target.value)}
                  placeholder={t("inputField.placeholder.enterNumberOfCams")}
                  min="1"
                  max="50"
                />
                <button
                  className="btn btn-primary"
                  style={{ minWidth: "100px" }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddRows();
                  }}
                  disabled={!rowCount || parseInt(rowCount) <= 0}
                >
                  {t("cctv.addCams")}
                </button>
              </div>

              {/* CCTV List Table */}
              <UnlimitTable
                columns={columns}
                dataSource={cctvList}
                rowKey={(record) => record.id}
                loading={loading}
                pagination={false}
                scroll={{ x: 800 }}
                className="mt-4"
              />

              {cctvList.length === 0 && !loading && (
                <div className="text-center py-4">
                  <p className="text-muted">{t("cctv.noCamerasAdded")}</p>
                </div>
              )}

              {/* Form Actions */}
              <div className="col-lg-12 mb-4 mt-4">
                <Link to={all_routes.cctvList} className="me-2">
                  <button type="button" className="btn btn-cancel">
                    {t("general.button.cancel")}
                  </button>
                </Link>
                <UnlimitSubmitButton
                  label={
                    id ? t("general.button.update") : t("general.button.submit")
                  }
                  isLoading={isLoading}
                  disabled={cctvList.length === 0}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CCTVForm;
